import { format } from "date-fns";
import { FileText, UserRound, Heart, Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useMemo, useState } from "react";
import { ClinicalNoteModal } from "./ClinicalNoteModal";
import { ClinicalDataLanguageNotice } from "@/components/ClinicalDataLanguageNotice";
import {
  deMarkdownSnippet,
  filterAndSortNotes,
  noteTypeOptions,
  type NoteFilter,
  type SortOrder,
  type TimeWindow,
} from "@/lib/note-filters";

export interface ClinicalNote {
  id: string | number;
  authorName: string;
  authorTitle: string;
  noteType: string;
  content: string;
  createdAt: string;
}

interface ClinicalNotesProps {
  notes: ClinicalNote[];
  onAskQuestion?: (question: string, displayText?: string) => void;
  admissionStart?: string;
  enableControls?: boolean;
}

const SELECT_CLASS =
  "text-xs border border-gray-300 rounded px-2 py-1 bg-white text-text-primary";

export default function ClinicalNotes({
  notes,
  onAskQuestion,
  admissionStart,
  enableControls = true,
}: ClinicalNotesProps) {
  const { t } = useTranslation();
  const [selectedNote, setSelectedNote] = useState<ClinicalNote | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [windowFilter, setWindowFilter] = useState<TimeWindow>(
    admissionStart ? "admission" : "all",
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");

  const types = useMemo(() => noteTypeOptions(notes), [notes]);
  const visible = useMemo(() => {
    if (!enableControls) {
      return filterAndSortNotes(notes, { type: "all", window: "all", sort: "newest" });
    }
    const filter: NoteFilter = { type: typeFilter, window: windowFilter, sort: sortOrder, admissionStart };
    return filterAndSortNotes(notes, filter);
  }, [notes, enableControls, typeFilter, windowFilter, sortOrder, admissionStart]);

  const handleViewNote = (note: ClinicalNote) => {
    setSelectedNote(note);
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedNote(null);
  };

  const getNoteTypeColor = (noteType: string) => {
    switch (noteType.toLowerCase()) {
      case "progress note":
        return "border-primary bg-blue-50";
      case "nursing":
        return "border-secondary bg-gray-50";
      case "consult note":
        return "border-success bg-green-50";
      case "discharge summary":
        return "border-accent bg-orange-50";
      default:
        return "border-gray-300 bg-gray-50";
    }
  };
  const getNoteIcon = (noteType: string) => {
    switch (noteType.toLowerCase()) {
      case "progress note":
        return <UserRound className="h-3.5 w-3.5" />;
      case "nursing":
        return <Heart className="h-3.5 w-3.5" />;
      default:
        return <FileText className="h-3.5 w-3.5" />;
    }
  };

  if (!notes || notes.length === 0) {
    return (
      <div className="text-center py-8 text-text-secondary">
        <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>{t('records.noClinicalNotesAvailable')}</p>
      </div>
    );
  }

  return (
    <>
      <ClinicalDataLanguageNotice />
      {enableControls && (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <select
            className={SELECT_CLASS}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label={t('records.filterByDocumentType')}
          >
            <option value="all">{t('records.allTypes')}</option>
            {types.map((noteType) => (
              <option key={noteType} value={noteType}>{noteType}</option>
            ))}
          </select>
          <select
            className={SELECT_CLASS}
            value={windowFilter}
            onChange={(e) => setWindowFilter(e.target.value as TimeWindow)}
            aria-label={t('records.filterByTime')}
          >
            {admissionStart && <option value="admission">{t('records.thisAdmission')}</option>}
            <option value="24h">{t('records.last24h')}</option>
            <option value="48h">{t('records.last48h')}</option>
            <option value="7d">{t('records.last7Days')}</option>
            <option value="all">{t('records.allTime')}</option>
          </select>
          <button
            className={`${SELECT_CLASS} flex items-center gap-1`}
            onClick={() => setSortOrder((s) => (s === "newest" ? "oldest" : "newest"))}
            aria-label={t('records.toggleSortOrder')}
          >
            {sortOrder === "newest" ? t('records.newestFirst') : t('records.oldestFirst')}
          </button>
        </div>
      )}

      <div className="relative">
        <div className="max-h-80 overflow-y-auto space-y-2">
          {visible.length === 0 ? (
            <p className="text-xs text-text-secondary text-center py-4">
              {t('records.noNotesMatchFilters')}
            </p>
          ) : (
            visible.map((note) => (
              <div
                key={note.id}
                className={`border-l-4 p-2 rounded-r-lg hover:bg-opacity-80 transition-colors cursor-pointer ${getNoteTypeColor(note.noteType)}`}
                onClick={() => handleViewNote(note)}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center space-x-2">
                    {getNoteIcon(note.noteType)}
                    <h3 className="text-xs font-medium text-text-primary">{note.noteType}</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-[11px] text-text-secondary">
                      {note.authorName} • {format(new Date(note.createdAt), "MMM d, h:mm a")}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewNote(note);
                      }}
                      className="h-5 w-5 p-0"
                      aria-label={t('records.viewNote')}
                    >
                      <Eye className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-text-primary leading-snug line-clamp-2">
                  {deMarkdownSnippet(note.content)}
                </p>
              </div>
            ))
          )}
        </div>
      </div>

      <ClinicalNoteModal
        note={selectedNote}
        open={modalOpen}
        onClose={handleCloseModal}
        onAskQuestion={onAskQuestion}
      />
    </>
  );
}
